import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { Folder, ChevronRight, Home } from 'lucide-react';
import { API_ENDPOINTS } from '../../../config/api.config';
import {apiService} from "../../../services/apiService.ts";

interface FolderData {
    id: string;
    name: string;
    parent_id: string | null;
    is_root: boolean;
    file_count: string | number;
    path: string[];
    level: number;
    created_at: string;
    updated_at: string;
    created_by_name: string;
    description: string | null;
}

interface RootFolder {
    id: string;
    name: string;
    description: string;
    parent_id: null;
    is_root: true;
    level: 0;
    path: string[];
    file_count?: number;
}

interface FolderHierarchyResponse {
    rootFolder: RootFolder | null;
    folders: FolderData[];
    total: number;
}

interface FolderSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onMoveToFolder: (targetFolderId: string | null, fullPath?: Array<{ id: string; name: string }>) => Promise<void>;
    currentFolderId: string | null;
    sourceFolderId?: string;
}

const FolderSelectionModal: React.FC<FolderSelectionModalProps> = ({
                                                                       isOpen,
                                                                       onClose,
                                                                       onMoveToFolder,
                                                                       currentFolderId,
                                                                       sourceFolderId
                                                                   }) => {
    const [folders, setFolders] = useState<FolderData[]>([]);
    const [rootFolder, setRootFolder] = useState<RootFolder | null>(null);
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isMoving, setIsMoving] = useState<boolean>(false);

    const fetchFolderHierarchy = async () => {
        try {
            return await apiService.get<FolderHierarchyResponse>(API_ENDPOINTS.DATA_SOURCE.LIST_FOLDERS_HIERARCHY);
        } catch (err) {
            console.error('Error fetching folders:', err);
            throw err;
        }
    };

    // Build path to a specific folder
    const buildFolderPath = (folderId: string | null): Array<{ id: string; name: string }> => {
        if (!folderId || !rootFolder || folderId === rootFolder.id) return [];

        const path: Array<{ id: string; name: string }> = [];
        let currentFolder = folders.find(f => f.id === folderId);

        while (currentFolder && currentFolder.id !== rootFolder.id) {
            path.unshift({ id: currentFolder.id, name: currentFolder.name });
            currentFolder = folders.find(f => f.id === currentFolder?.parent_id);
        }

        return path;
    };

    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            setError(null);
            setSelectedFolderId(null);

            fetchFolderHierarchy()
                .then((data) => {
                    setFolders(data.folders);
                    setRootFolder(data.rootFolder);

                    // Auto-expand to current folder
                    if (currentFolderId) {
                        const parentsToExpand = new Set<string>();
                        let currentParentId = currentFolderId;

                        while (currentParentId) {
                            const parentFolder = data.folders.find(f => f.id === currentParentId);
                            if (parentFolder?.parent_id) {
                                parentsToExpand.add(parentFolder.parent_id);
                                currentParentId = parentFolder.parent_id;
                            } else {
                                break;
                            }
                        }

                        setExpandedFolders(parentsToExpand);
                    }
                })
                .catch(() => {
                    setError('Failed to load folders');
                })
                .finally(() => setIsLoading(false));
        }
    }, [isOpen, currentFolderId]);

    const handleMove = async () => {
        try {
            setIsMoving(true);
            setError(null);

            // If selectedFolderId is null, use the root folder ID if available
            const targetFolderId = selectedFolderId === null ? rootFolder?.id || null : selectedFolderId;

            // Build the complete path to the target folder
            const fullPath = buildFolderPath(targetFolderId);

            // Pass both the target folder ID and the full path
            await onMoveToFolder(targetFolderId, fullPath);
            onClose();
        } catch (err) {
            setError('Failed to move items');
            console.error('Move error:', err);
        } finally {
            setIsMoving(false);
        }
    };

    const handleFolderClick = (folderId: string, e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        setExpandedFolders(prev => {
            const newSet = new Set(prev);
            if (newSet.has(folderId)) {
                newSet.delete(folderId);
            } else {
                newSet.add(folderId);
            }
            return newSet;
        });
    };

    const buildFolderTree = (parentId: string | null): React.ReactNode[] => {
        const currentLevelFolders = folders.filter(folder => folder.parent_id === parentId);

        return currentLevelFolders.map((folder: FolderData) => {
            const isExpanded = expandedFolders.has(folder.id);
            const isSelected = selectedFolderId === folder.id;

            const isSubfolderOf = (folder: FolderData, targetId: string): boolean => {
                let current = folder;
                while (current && current.parent_id) {
                    if (current.parent_id === targetId) {
                        return true;
                    }
                    current = folders.find(f => f.id === current.parent_id) || { ...current, parent_id: null };
                }
                return false;
            };

            const isDisabled = sourceFolderId ? (
                folder.id === sourceFolderId || // Selected folder
                isSubfolderOf(folder, sourceFolderId) // Any subfolder of selected folder
            ) : false;

            const hasChildren = folders.some(f => f.parent_id === folder.id);
            const fileCount = typeof folder.file_count === 'string'
                ? parseInt(folder.file_count, 10)
                : folder.file_count;

            const indentationWidth = (folder.level - 1) * 16 + 'px';

            return (
                <div key={folder.id} className="folder-item">
                    <div
                        className={`
                        folder-row
                        d-flex align-items-center p-2
                        ${isDisabled ? 'opacity-50' : 'cursor-pointer'}
                        ${isSelected ? 'bg-secondary bg-opacity-25' : ''} 
                        ${!isDisabled && !isSelected ? 'hover:bg-light' : ''}
                    `.trim()}
                        onClick={() => !isDisabled && setSelectedFolderId(folder.id)}
                        title={isDisabled ? "Can't move to this folder" : undefined}
                    >
                        <div style={{ width: indentationWidth, flexShrink: 0 }} />
                        <div
                            className="chevron-container"
                            style={{
                                width: '24px',
                                visibility: hasChildren ? 'visible' : 'hidden',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexShrink: 0
                            }}
                            onClick={(e) => handleFolderClick(folder.id, e)}
                        >
                            <ChevronRight
                                className="transition-transform"
                                style={{
                                    transform: isExpanded ? 'rotate(90deg)' : 'none',
                                    transition: 'transform 0.2s ease'
                                }}
                                size={16}
                            />
                        </div>
                        <Folder size={16} className="me-2 text-primary" style={{ flexShrink: 0 }} />
                        <div className="folder-info flex-grow-1">
                            <span className="folder-name text-sm">
                                {folder.name}
                                {fileCount > 0 && (
                                    <small className="text-muted ms-2">
                                        ({fileCount} files)
                                    </small>
                                )}
                            </span>
                        </div>
                    </div>
                    {isExpanded && hasChildren && (
                        <div className="folder-children">
                            {buildFolderTree(folder.id)}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <Modal show={isOpen} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Select Destination Folder</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">{error}</Alert>
                )}

                <div className="folder-tree border rounded" style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    backgroundColor: '#ffffff'
                }}>
                    {/* Root Home option */}
                    <div
                        className={`
                            d-flex align-items-center p-2 cursor-pointer
                            ${selectedFolderId === null ? 'bg-secondary bg-opacity-25' : ''}
                            ${selectedFolderId !== null ? 'hover:bg-light' : ''}
                        `}
                        style={{ borderBottom: '1px solid #e5e7eb' }}
                        onClick={() => setSelectedFolderId(null)}
                    >
                        <Home size={16} className="me-2 text-primary" />
                        <div className="folder-info flex-grow-1">
                            <span className="folder-name text-sm">
                                Home
                                {rootFolder?.file_count && rootFolder.file_count > 0 && (
                                    <small className="text-muted ms-2">
                                        ({rootFolder.file_count} files)
                                    </small>
                                )}
                            </span>
                        </div>
                    </div>

                    <div className="folder-list p-1">
                        {rootFolder && buildFolderTree(rootFolder.id)}
                    </div>

                    {isLoading && (
                        <div className="text-center p-3">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading folders...
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleMove}
                    disabled={isLoading || isMoving || Boolean(sourceFolderId && selectedFolderId === sourceFolderId)}
                >
                    {isMoving ? 'Moving...' : 'Move Here'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default FolderSelectionModal;